import {Navigate} from 'react-router-dom';
import {PageLayout} from './layouts/PageLayout';
import {Login} from './pages/Login';
import {SignUp} from './pages/SignUp';
import {Locations} from './pages/Locations';
import {Welcome} from './pages/Welcome';
import {DashboardLayout} from './layouts/DashboardLayout';
import {LocationInfo} from './pages/LocationInfo';
import {Users} from './pages/Users';
import {UserInfo} from './pages/UserInfo';
import {Chargers} from './pages/Chargers';
import {Tariffs} from './pages/Tariffs';
import {ForgotPassword} from './pages/ForgotPassword';
import {ResetPassword} from './pages/ResetPassword';
import {SetPassword} from './pages/SetPassword';
import {Contracts} from './pages/Contracts';
import {BusinessInfo} from './pages/BusinessInfo';
import {ChargerInfo} from './pages/ChargerInfo';
import {ContractInfo} from './pages/ContractInfo';
import {TariffInfo} from './pages/TariffInfo';
import {Reports} from './pages/Reports';
import {ConfirmRegistartion} from './pages/ConfirmRegistartion';
import {ContractStatusEnum, RegistrationStatusEnum} from './enums';
import {personaOf} from './permissions/hooks';
import {FALLBACK_ROUTE, resolveLandingRoute} from './utils/resolveLandingRoute';
import {PermissionRoute} from './components/common/PermissionRoute';
import {PAGE_PERMISSION} from './permissions/pages';
import {AppRouteEnum} from './utils/helper';
import {ErrorPage} from './pages/404';
import {store} from './app/store';
import {VerifyEmail} from './pages/VerifyEmail';
import {CorporateUserWelcome} from './pages/CorporateUserWelcome';
import {Invoices} from './pages/Invoices';
import {InvoiceInfo} from './pages/InvoiceInfo';
import {DetailedChargingSessions} from './pages/DetailedChargingSessions';
import {ChargingSessionInfo} from './pages/ChargingSessionInfo';
import {ChargerConfigurations} from './pages/ChargerConfigurations';
import {ChargerLogs} from './pages/ChargerLogs';
import {EnergyConsumptionReport} from './pages/EnergyConsumptionReport';
import {Complete} from './pages/Complete';
import {TermsOfUse} from './pages/TermsOfUse';
import {TermsOfUseFr} from './pages/TermsOfUseFr';
import {TermsOfUseDe} from './pages/TermsOfUseDe';
import {PrivacyPolicy} from './pages/PrivacyPolicy';
import {PrivacyPolicyFrench} from './pages/PrivacyPolicyFrench';
import {Support} from './pages/Support';
import {SupportFr} from './pages/SupportFr';
import {Notifications} from './pages/Notifications';
import {TechnicalTeam} from './pages/TechnicalTeam';
import {TechnicalTeamInfo} from './pages/TechnicalTeamInfo';
import {NotificationInfo} from './pages/NotificationInfo';
import {PrivacyPolicyDeutsch} from './pages/PrivacyPolicyDeutsch';
import {SupportDe} from './pages/SupportDe';
import {ContractDTO} from './types';
import {ReOwner} from './pages/ReOwner';
import {ReManager} from './pages/ReManager/Index';
import {Rfid} from './pages/Rfid/Index';
import {RfidPage} from './pages/RfidPage/Index';
import {EmployeePage} from './pages/EmployeePage/Index';
import {EmployeeInfo} from './pages/EmployeeInfo';
import {DynamicPricing} from './pages/DynamicPricing';
import {GuestCharging} from './pages/GuestCharging';
import {AiAgent} from './pages/AiAgent';
import {Businesses} from './pages/Businesses';
import {BusinessProfile} from './pages/BusinessProfile';
import {AccessControl} from './pages/AccessControl';
import {PermissionInfo} from './pages/PermissionInfo';
import {RoleAccess} from './pages/RoleAccess';
import {UserAccess} from './pages/UserAccess';
import {MoreMenu} from './pages/MoreMenu';

const routes = () => {
    // Defaulted, not merely optional-chained. A state persisted by an older build can rehydrate with no
    // `authInfo` at all, and `Object.keys(undefined)` below threw inside routes() - which runs from
    // App before React has rendered anything, so the whole application came out as a blank page with
    // nothing on screen to say why. It only ever hit people who had used the site before: a fresh
    // profile starts from `{}` and works, which is why it looked like a browser problem.
    const authinfo = store?.getState()?.common?.authInfo ?? {};

    // routes() is a plain function, not a component, so it reads the store rather than using hooks.
    const access = store?.getState()?.common?.access;
    const persona = personaOf(access);
    const isDriver = persona.isDriver;
    const isCompany = persona.isCompany;
    const isStatusOperation = authinfo?.contracts?.find(
        (contract: ContractDTO) => contract?.status === ContractStatusEnum.OPERATION
    );
    const isChargePointAvailable =
        Object.keys(authinfo)?.length && !isDriver && authinfo?.chargePoint;
    const registrationStatus = authinfo.registrationStatus;
    const profileActive = registrationStatus === RegistrationStatusEnum.ACTIVATED;

    return [
        {
            path: 'app',
            element: localStorage.getItem('auth') ? <DashboardLayout/> : <Navigate to="/login"/>,
            children: [
                {
                    index: true,
                    // Nothing until the permissions arrive. Deciding a landing page from an empty
                    // access object sent everyone to /app/welcome — the registration screen — and the
                    // real destination only appeared once the answer came back and this was
                    // recomputed. A blank frame is honest; a wrong page that corrects itself is not.
                    element: !access ? null : isDriver && !!isStatusOperation
                    && !isChargePointAvailable && profileActive ? (
                        <Navigate to={`/app/contracts/${isStatusOperation?.id}`}/>
                    ) : isDriver ? (
                        <Navigate to="/app/welcome"/>
                    ) : (
                        // Everyone else — a company, back office, real estate, super admin —
                        // lands on the first page their permissions allow. They used to be sent to the
                        // registration stepper and bounced off it by an effect inside the page, which
                        // is why it flashed before the real page appeared.
                        <Navigate to={resolveLandingRoute(access)}/>
                    ),
                },
                {
                    path: 'welcome',
                    // Welcome is the driver and company home, and it is also the last resort for
                    // someone whose permissions open no other page. Redirecting away without that
                    // second case is a loop: the fallback route is this one, so it would send them
                    // straight back here.
                    element: !access ? null
                        : (isDriver || isCompany || resolveLandingRoute(access) === FALLBACK_ROUTE) ? (
                            <Welcome/>
                        ) : (
                            <Navigate to={resolveLandingRoute(access)} replace/>
                        ),
                },
                {
                    path: 'locations',
                    element: (
                        <PermissionRoute permission={PAGE_PERMISSION[AppRouteEnum.locations]}>
                            <Locations/>
                        </PermissionRoute>
                    ),
                },
                {
                    path: 'locations/:id',
                    element: (
                        <PermissionRoute permission="locations.read">
                            <LocationInfo/>
                        </PermissionRoute>
                    ),
                },
                {
                    path: 'locations/:locationId/prices/:priceId',
                    element: (
                        <PermissionRoute permission="prices.read">
                            <DynamicPricing/>
                        </PermissionRoute>
                    ),
                },
                {
                    path: 'users',
                    element: (
                        <PermissionRoute permission={PAGE_PERMISSION[AppRouteEnum.users]}>
                            <Users/>
                        </PermissionRoute>
                    ),
                },
                {
                    path: 'users/:id',
                    element: (
                        <PermissionRoute permission="users.read">
                            <UserInfo/>
                        </PermissionRoute>
                    ),
                },
                {
                    path: 'users/employee/:id',
                    element: (
                        <PermissionRoute permission="employees.read">
                            <EmployeeInfo/>
                        </PermissionRoute>
                    ),
                },
                {
                    path: 'chargers',
                    element: (
                        <PermissionRoute permission={PAGE_PERMISSION[AppRouteEnum.chargers]}>
                            <Chargers/>
                        </PermissionRoute>
                    ),
                },
                {
                    path: 'chargers/:id',
                    element: (
                        <PermissionRoute permission="chargers.read">
                            <ChargerInfo/>
                        </PermissionRoute>
                    ),
                },
                {
                    path: 'chargers/:id/configurations',
                    element: (
                        <PermissionRoute permission="chargers.readConfig">
                            <ChargerConfigurations/>
                        </PermissionRoute>
                    ),
                },
                {
                    path: 'chargers/:id/logs',
                    element: (
                        <PermissionRoute permission="chargerLogs.search">
                            <ChargerLogs/>
                        </PermissionRoute>
                    ),
                },
                {
                    path: 'charging-sessions',
                    element: (
                        <PermissionRoute permission={PAGE_PERMISSION[AppRouteEnum.chargingSessions]}>
                            <DetailedChargingSessions/>
                        </PermissionRoute>
                    ),
                },
                {
                    path: 'charging-sessions/:id',
                    element: (
                        <PermissionRoute permission="transactions.read">
                            <ChargingSessionInfo/>
                        </PermissionRoute>
                    ),
                },
                {
                    path: 'tariffs',
                    element: (
                        <PermissionRoute permission={PAGE_PERMISSION[AppRouteEnum.tariffs]}>
                            <Tariffs/>
                        </PermissionRoute>
                    ),
                },
                {
                    path: 'tariffs/:id',
                    element: (
                        <PermissionRoute permission="tariffs.read">
                            <TariffInfo/>
                        </PermissionRoute>
                    ),
                },
                {
                    path: 'contracts',
                    element: (
                        <PermissionRoute permission={PAGE_PERMISSION[AppRouteEnum.contracts]}>
                            <Contracts/>
                        </PermissionRoute>
                    ),
                },
                {
                    path: 'contracts/:id',
                    element: (
                        <PermissionRoute permission="contracts.read">
                            <ContractInfo/>
                        </PermissionRoute>
                    ),
                },
                {
                    path: 'users/business/:id',
                    element: (
                        <PermissionRoute permission="businesses.read">
                            <BusinessInfo/>
                        </PermissionRoute>
                    ),
                },
                {
                    path: 'reports',
                    element: (
                        <PermissionRoute permission={PAGE_PERMISSION[AppRouteEnum.reports]}>
                            <Reports/>
                        </PermissionRoute>
                    ),
                },
                {
                    path: 'reports/energy-consumption',
                    // Reached from the statistics page, and gated the same way. Its own data comes
                    // from report-service, which asks only that you are signed in and clamps what you
                    // see to your own slice - so a driver reading their consumption is exactly what it
                    // is for. It was guarded on reports.read, which it never calls, and the guard
                    // bounced them to whatever page they could open instead.
                    element: (
                        <PermissionRoute permission={PAGE_PERMISSION[AppRouteEnum.reports]}>
                            <EnergyConsumptionReport/>
                        </PermissionRoute>
                    ),
                },
                {
                    path: 'reports/charging-sessions',
                    element: (
                        // The same page as /app/charging-sessions, so the same permission. It was
                        // guarded on reports.read, which let a report reader onto a page that fetches
                        // transactions; the page then bounced them to /404 from its own effect.
                        <PermissionRoute permission={PAGE_PERMISSION[AppRouteEnum.chargingSessions]}>
                            <DetailedChargingSessions/>
                        </PermissionRoute>
                    ),
                },
                {
                    path: 'assistant',
                    element: (
                        <PermissionRoute permission={PAGE_PERMISSION[AppRouteEnum.assistant]}>
                            <AiAgent/>
                        </PermissionRoute>
                    ),
                },
                {
                    path: 'invoices',
                    element: (
                        <PermissionRoute permission={PAGE_PERMISSION[AppRouteEnum.invoices]}>
                            <Invoices/>
                        </PermissionRoute>
                    ),
                },
                {
                    path: 'invoices/:id',
                    element: (
                        <PermissionRoute permission="invoices.read">
                            <InvoiceInfo/>
                        </PermissionRoute>
                    ),
                },
                {
                    path: 'technical-team',
                    element: (
                        <PermissionRoute permission={PAGE_PERMISSION[AppRouteEnum.technicalTeam]}>
                            <TechnicalTeam/>
                        </PermissionRoute>
                    ),
                },
                {
                    path: 'technical-team/:id',
                    element: (
                        <PermissionRoute permission="users.read">
                            <TechnicalTeamInfo/>
                        </PermissionRoute>
                    ),
                },
                {
                    path: 'notifications',
                    element: (
                        <PermissionRoute permission={PAGE_PERMISSION[AppRouteEnum.notifications]}>
                            <Notifications/>
                        </PermissionRoute>
                    ),
                },
                {
                    path: 'notifications/:id',
                    element: (
                        <PermissionRoute permission="notifications.read">
                            <NotificationInfo/>
                        </PermissionRoute>
                    ),
                },
                {
                    path: 're-owners',
                    element: (
                        <PermissionRoute permission={PAGE_PERMISSION[AppRouteEnum.reOwner]}>
                            <ReOwner/>
                        </PermissionRoute>
                    ),
                },
                {
                    path: 're-owners/:id',
                    element: (
                        <PermissionRoute permission="realEstates.read">
                            <ReManager/>
                        </PermissionRoute>
                    ),
                },
                {
                    path: 'rfids',
                    element: (
                        <PermissionRoute permission={PAGE_PERMISSION[AppRouteEnum.rfid]}>
                            <Rfid/>
                        </PermissionRoute>
                    ),
                },
                {
                    path: 'rfids/:id',
                    element: (
                        <PermissionRoute permission="rfidTags.read">
                            <RfidPage/>
                        </PermissionRoute>
                    ),
                },
                {
                    path: 'businesses',
                    element: (
                        <PermissionRoute permission={PAGE_PERMISSION[AppRouteEnum.businesses]}>
                            <Businesses/>
                        </PermissionRoute>
                    ),
                },
                {
                    // The single business, on its own route rather than borrowing the user screen:
                    // a business is not a person, and that screen answered a different question.
                    path: 'businesses/:id',
                    element: (
                        <PermissionRoute permission={PAGE_PERMISSION[AppRouteEnum.businesses]}>
                            <BusinessProfile/>
                        </PermissionRoute>
                    ),
                },
                {
                    path: 'access-control',
                    element: (
                        <PermissionRoute permission={PAGE_PERMISSION[AppRouteEnum.accessControl]}>
                            <AccessControl/>
                        </PermissionRoute>
                    ),
                },
                {
                    path: 'access-control/roles/:roleId',
                    element: (
                        <PermissionRoute permission="accessControl.readRoles">
                            <RoleAccess/>
                        </PermissionRoute>
                    ),
                },
                {
                    // Reading someone's effective permissions is itself a binding-management right on
                    // the server: it discloses the whole shape of another person's access.
                    path: 'access-control/users/:userId',
                    element: (
                        <PermissionRoute permission="accessControl.manageBindings">
                            <UserAccess/>
                        </PermissionRoute>
                    ),
                },
                {
                    path: 'access-control/permissions/:permissionName',
                    element: (
                        <PermissionRoute permission="accessControl.readCatalog">
                            <PermissionInfo/>
                        </PermissionRoute>
                    ),
                },
                {
                    path: 'employees',
                    element: (
                        <PermissionRoute permission={PAGE_PERMISSION[AppRouteEnum.employee]}>
                            <EmployeePage/>
                        </PermissionRoute>
                    ),
                },
                {
                    // The phone's menu screen, and no permission of its own: it renders the entries
                    // the viewer's permissions already allow, each of which keeps its own guard. On a
                    // desktop it redirects, since the drawer is standing open there.
                    path: 'more',
                    element: <MoreMenu/>,
                },
            ],
        },
        {
            path: '/',
            element: <PageLayout/>,
            children: [
                {
                    index: true,
                    element: !localStorage.getItem('auth') && <Navigate to="/login"/>,
                },
                {
                    path: 'login',
                    element: <Login/>,
                },
                {
                    path: 'signUp',
                    element: <SignUp/>,
                },
                {
                    path: 'forgot',
                    element: <ForgotPassword/>,
                },
                {
                    path: 'reset-password',
                    element: <ResetPassword/>,
                },
                {
                    path: 'set-password',
                    element: <SetPassword/>,
                },
                {
                    path: 'completeRegistration',
                    element: <Complete/>,
                },
                {
                    path: 'confirmRegistration',
                    element: <ConfirmRegistartion/>,
                },
                {
                    path: 'verifyEmail',
                    element: <VerifyEmail/>,
                },
                {
                    path: 'corporateUserWelcome',
                    element: <CorporateUserWelcome/>,
                },
                {
                    path: 'privacy-policy',
                    element: <Navigate to={`/privacy-policy/${store.getState().common.lang}`}/>,
                },
                {
                    path: 'privacy-policy/fr',
                    element: <PrivacyPolicyFrench/>,
                },
                {
                    path: 'privacy-policy/de',
                    element: <PrivacyPolicyDeutsch/>,
                },
                {
                    path: 'terms-of-use',
                    element: <TermsOfUse/>,
                },
                {
                    path: 'terms-of-use/en',
                    element: <TermsOfUse/>,
                },
                {
                    path: 'terms-of-use/fr',
                    element: <TermsOfUseFr/>,
                },
                {
                    path: 'terms-of-use/de',
                    element: <TermsOfUseDe/>,
                },
                {
                    path: 'privacy-policy/en',
                    element: <PrivacyPolicy/>,
                },
                {
                    path: 'support',
                    element: <Navigate to={`/support/${store.getState().common.lang}`}/>,
                },
                {
                    path: 'support/en',
                    element: <Support/>,
                },
                {
                    path: 'support/fr',
                    element: <SupportFr/>,
                },

                {
                    path: 'support/de',
                    element: <SupportDe/>,
                },
            ],
        },
        {
            path: 'guest/charge/:connectorUuid',
            element: <GuestCharging />,
        },
        {
            path: '*',
            element: <ErrorPage/>,
        },
    ];
};

export default routes;
